import { Box, Typography, IconButton, Drawer } from '@mui/material';
import { Fragment } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AirportTimelineSlot from './AirportTimelineSlot';
import { SelectedAirports } from '../../hooks/map/useAirportSelection';
import { HourlyForecastTimelineData, ErrorInfo } from '../../models/HourlyForecastTimelineData';
import * as styles from '../../styles/MapPageStyles';

// Extended timeline data interface
interface ExtendedTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
}

export interface TimelineDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedAirports: SelectedAirports;
  airportTimelineData: Record<number, ExtendedTimelineData>;
  onViewAllTimelines: () => void;
}

/**
 * TimelineDrawer component
 *
 * Displays a side drawer with up to 3 airport forecast timelines.
 * Used in the MapPage to show detailed forecasts for selected airports.
 */
export function TimelineDrawer({
  open,
  onClose,
  selectedAirports,
  airportTimelineData,
  onViewAllTimelines
}: TimelineDrawerProps) {
  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: styles.drawerPaperStyles
      }}
      ModalProps={{
        sx: styles.drawerModalStyles
      }}
    >
      <Box sx={styles.drawerContentStyles}>
        {/* Close button */}
        <Box sx={styles.drawerCloseButtonContainerStyles}>
          <IconButton
            onClick={onClose}
            sx={styles.drawerCloseButtonStyles}
            aria-label="Close sidebar"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Airport Forecast section */}
        <Box sx={styles.airportForecastContainerStyles}>
          <Box sx={styles.airportForecastHeaderStyles}>
            <Box sx={styles.airportForecastHeaderFlexStyles}>
              <Typography variant="h5" sx={styles.airportForecastTitleStyles}>
                Airport Forecasts
              </Typography>
              <Typography
                variant="caption"
                sx={styles.viewAllTimelineLinkStyles}
                onClick={onViewAllTimelines}
              >
                View all timelines
              </Typography>
            </Box>
          </Box>

          {/* Timelines */}
          <Box sx={styles.dualTimelineContainerStyles}>
            {selectedAirports.map((airport, index) => (
              <Fragment key={index}>
                {index > 0 && (
                  <Box sx={styles.timelineDividerStyles} />
                )}
                <AirportTimelineSlot
                  selectedAirport={airport}
                  timelineData={airport ? (airportTimelineData[airport.id] || null) : null}
                />
              </Fragment>
            ))}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
